<template>
  <b-modal
    id="ai-recall-cadence-modal"
    v-model="show"
    title="AI Recall — follow-up settings"
    size="lg"
    :ok-disabled="saving || errors.length > 0"
    :ok-title="saving ? 'Saving…' : 'Save settings'"
    ok-variant="success"
    @ok.prevent="save"
    @show="load"
  >
    <div class="cadence">
      <b-alert v-if="form.corrupt" show variant="danger">
        <strong>These settings could not be read.</strong>
        Saving will <strong>replace</strong> them, and the previous configuration cannot be
        recovered. Until then, AI Recall follow-ups are switched off for this office.
        <b-form-checkbox v-model="confirmReplaceCorrupt" class="mt-2">
          I understand — replace the unreadable settings
        </b-form-checkbox>
      </b-alert>

      <b-alert v-if="errors.length" show variant="warning">
        <div v-for="(e, i) in errors" :key="i">{{ e }}</div>
      </b-alert>

      <!-- Master switch gets its own card so the on/off decision reads as the PARENT of
           everything below, not as one more field in a flat list. -->
      <div class="cadence-master" :class="{ 'is-on': form.activated }">
        <b-form-checkbox v-model="form.activated" switch class="cadence-switch">
          <span class="cadence-master-title">Turn on AI Recall follow-ups</span>
        </b-form-checkbox>
        <p class="cadence-help mb-0">
          When an AI recall call isn't answered we text the patient right away. If they still
          haven't booked, we can follow up by email.
        </p>
      </div>

      <!-- Dependent settings, visually nested under the master switch. -->
      <div class="cadence-panel" :class="{ 'is-disabled': !form.activated }">
        <p v-if="!form.activated" class="cadence-locked">
          Turn the switch on to change these.
        </p>

        <fieldset :disabled="!form.activated">
          <div class="cadence-row">
            <div class="cadence-row-label">
              <label for="cadence-email-toggle">Email follow-up</label>
              <span class="cadence-help">Sent only if they still haven't booked.</span>
            </div>
            <div class="cadence-row-control">
              <b-form-checkbox id="cadence-email-toggle" v-model="form.emailEnabled" switch>
                {{ form.emailEnabled ? 'On' : 'Off' }}
              </b-form-checkbox>
            </div>
          </div>

          <div class="cadence-row">
            <div class="cadence-row-label">
              <label for="cadence-delay">Send after</label>
              <span class="cadence-help">0 sends as soon as we notice. Max {{ maxDelayHours }}h (~2 weeks).</span>
            </div>
            <div class="cadence-row-control">
              <div class="cadence-inline">
                <input
                  id="cadence-delay"
                  v-model.number="form.delayHours"
                  type="number"
                  min="0"
                  :max="maxDelayHours"
                  step="1"
                  class="form-control cadence-num"
                />
                <span class="cadence-unit">hours after the call</span>
              </div>
            </div>
          </div>

          <div class="cadence-row">
            <div class="cadence-row-label">
              <label>Channels</label>
              <span class="cadence-help">Email follow-ups need Email selected.</span>
            </div>
            <div class="cadence-row-control">
              <!-- The chip border goes on a WRAPPER, never on b-form-checkbox itself.
                   b-form-checkbox renders Bootstrap's .custom-control, which already carries
                   padding-left:1.5rem with the box absolutely positioned at left:-1.5rem on
                   its label. Adding our own left padding to that element pushed the box out
                   through the rounded border and clipped the text. -->
              <div class="cadence-channels">
                <div class="cadence-chip" :class="{ 'is-checked': form.channelOrder.includes('sms') }">
                  <b-form-checkbox v-model="form.channelOrder" value="sms">Text message</b-form-checkbox>
                </div>
                <div class="cadence-chip" :class="{ 'is-checked': form.channelOrder.includes('email') }">
                  <b-form-checkbox v-model="form.channelOrder" value="email">Email</b-form-checkbox>
                </div>
              </div>
            </div>
          </div>

          <div class="cadence-row cadence-row--last">
            <div class="cadence-row-label">
              <label for="cadence-quiet-start">Quiet hours</label>
              <span class="cadence-help">Office local time. Blank means no restriction.</span>
            </div>
            <div class="cadence-row-control">
              <div class="cadence-inline">
                <input id="cadence-quiet-start" v-model="form.quietStart" type="time" class="form-control cadence-time" />
                <span class="cadence-unit">to</span>
                <input v-model="form.quietEnd" type="time" class="form-control cadence-time" />
                <b-button
                  v-if="form.quietStart || form.quietEnd"
                  variant="link"
                  size="sm"
                  class="cadence-clear"
                  @click="clearQuiet"
                >
                  Clear
                </b-button>
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      <div class="cadence-summary" :class="{ 'is-on': form.activated }">
        <span class="cadence-summary-label">Current setting</span>
        <strong>{{ summary }}</strong>
      </div>
    </div>
  </b-modal>
</template>

<script>
/**
 * Per-site editor for the AI-Recall delayed email leg (Phase 5).
 *
 * Persists via `customer/upsertSettings` — NOT `customer/saveSettings`, which bulk-deletes
 * (see the comment on that action in src/store/customer.js). All parsing, validation and
 * payload building lives in src/utils/aiRecallCadence.js so it can be unit-tested without a
 * browser; this component is the thin shell.
 *
 * The backend treats an unreadable AI_RECALL_SETTINGS blob as OFF, deliberately — so a UI
 * bug that wrote invalid JSON would silently disable the feature rather than error.
 * buildSavePayload round-trips its own output to make that impossible from here.
 */
import {
  parseCadence,
  validateCadence,
  buildSavePayload,
  describeCadence,
  CADENCE_DEFAULTS,
  ACTIVATED_KEY,
  SETTINGS_KEY,
} from '@/utils/aiRecallCadence';

export default {
  name: 'AiRecallCadenceModal',
  props: {
    value: { type: Boolean, default: false },
    siteId: { type: String, required: true },
    allSettings: { type: Object, default: null },
  },
  data() {
    return {
      show: this.value,
      saving: false,
      confirmReplaceCorrupt: false,
      maxDelayHours: CADENCE_DEFAULTS.maxDelayHours,
      form: parseCadence(null, null),
    };
  },
  computed: {
    errors() {
      return validateCadence(this.form);
    },
    summary() {
      return describeCadence(this.form);
    },
  },
  watch: {
    value(v) { this.show = v; },
    show(v) { this.$emit('input', v); },
  },
  methods: {
    load() {
      const s = this.allSettings || {};
      const activated = s[ACTIVATED_KEY] ? s[ACTIVATED_KEY].PropertyValue : null;
      const blob = s[SETTINGS_KEY] ? s[SETTINGS_KEY].PropertyValue : null;
      this.form = parseCadence(activated, blob);
      this.confirmReplaceCorrupt = false;
    },
    clearQuiet() {
      this.form.quietStart = '';
      this.form.quietEnd = '';
    },
    async save() {
      // settingsLoaded guards the dangerous case: if this office's settings never loaded,
      // the form holds DEFAULTS, and saving them would silently overwrite the practice's
      // real configuration with values nobody chose.
      const built = buildSavePayload(this.form, {
        siteId: this.siteId,
        settingsLoaded: !!this.allSettings,
        confirmedReplaceCorrupt: this.confirmReplaceCorrupt,
      });
      if (!built.ok) {
        this.$toast.error(built.errors[0]);
        return;
      }
      this.saving = true;
      try {
        await this.$store.dispatch('customer/upsertSettings', {
          settings: built.settings,
          // Use the siteId the payload was BUILT for, not a re-read of the prop — it cannot
          // drift if the active site changed while the modal was open.
          siteID: built.siteId,
        });
        // Re-read so the panel reflects what was actually stored, not what we hoped.
        await this.$store.dispatch('customer/getSettings', { siteID: built.siteId });
        this.$toast.success('AI Recall follow-up settings saved.');
        this.$emit('saved');
        this.show = false;
      } catch (err) {
        this.$toast.error('Could not save those settings. Please try again.');
        console.error('AI Recall cadence save failed:', err);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$cadence-muted: #6c757d;
$cadence-line: #e4e7eb;

.cadence { font-size: 0.925rem; }

.cadence-help {
  display: block;
  color: $cadence-muted;
  font-size: 0.8rem;
  line-height: 1.35;
}

/* ── master switch ─────────────────────────────────────────────── */
.cadence-master {
  border: 1px solid $cadence-line;
  border-radius: 6px;
  padding: 0.9rem 1rem;
  background: #f8f9fa;
  transition: background 0.15s, border-color 0.15s;

  &.is-on {
    background: #f1f8f4;
    border-color: #b7dfc6;
  }
}
.cadence-master-title { font-weight: 600; }
/* The switch label must stay ONE line — nesting helper text inside it pushes the
   BootstrapVue toggle (absolutely positioned) out of vertical alignment. That was the
   original misalignment. */
.cadence-switch { margin-bottom: 0.35rem; }

/* ── dependent settings, nested under the master switch ────────── */
.cadence-panel {
  margin-top: 0.85rem;
  padding-left: 1rem;
  border-left: 3px solid $cadence-line;
  transition: opacity 0.15s;

  &.is-disabled { opacity: 0.6; }
}
.cadence-locked {
  color: $cadence-muted;
  font-size: 0.8rem;
  margin-bottom: 0.75rem;
}

/* Two columns on desktop so the modal width is actually used; stacks on mobile. */
.cadence-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.5rem 1rem;
  padding: 0.7rem 0;
  border-bottom: 1px solid $cadence-line;

  &--last { border-bottom: 0; }
}
.cadence-row-label {
  flex: 0 0 210px;
  max-width: 210px;

  label { margin-bottom: 0.1rem; font-weight: 600; }
}
.cadence-row-control { flex: 1 1 260px; min-width: 0; }

.cadence-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cadence-num { width: 90px; }
.cadence-time { width: 130px; }
.cadence-unit { color: $cadence-muted; font-size: 0.85rem; }
.cadence-clear { padding: 0 0.25rem; }

/* Channels read as selectable chips instead of two tiny left-hugging boxes.
   The border lives on THIS wrapper — b-form-checkbox keeps its own geometry untouched
   inside it, so the box stays put and the label never clips. */
.cadence-channels { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.cadence-chip {
  border: 1px solid $cadence-line;
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  background: #fff;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s;

  &.is-checked {
    background: #f1f8f4;
    border-color: #b7dfc6;
  }

  /* BootstrapVue's .custom-control ships its own margin-bottom; drop it so the chip
     hugs the control. Pierced with ::v-deep under a scoped ancestor so containment holds. */
  ::v-deep .custom-control {
    margin-bottom: 0;
    min-height: auto;
  }
  ::v-deep .custom-control-label {
    line-height: 1.4;
    cursor: pointer;
  }
}

/* ── summary ───────────────────────────────────────────────────── */
.cadence-summary {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.65rem 0.9rem;
  border-radius: 6px;
  background: #f1f3f5;

  &.is-on { background: #e8f5ec; }
}
.cadence-summary-label {
  color: $cadence-muted;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* ── mobile: labels sit above their controls ───────────────────── */
@media (max-width: 575.98px) {
  .cadence-row-label { flex: 1 1 100%; max-width: 100%; }
  .cadence-row-control { flex: 1 1 100%; }
  .cadence-panel { padding-left: 0.65rem; }
}
</style>
